import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PersonInfo, RadioButton, Select } from "ui";

import { DriverSelector } from "modules/drivers";
import { useGetDeliveryCompanies } from "modules/deliveryCompanies";
import {
  useAssignDeliveryCompany,
  useAssignDriver
} from "modules/orders";

import { ReturnStatus } from "../../../../../constants";
import { ReturnModalDriverBlockProps } from "../../ReturnModal.types";

import styles from "../../ReturnModal.module.scss";

const ASSIGNABLE_STATUSES = [
  ReturnStatus.ReturnPlaced,
  ReturnStatus.ReturnApproved,
  ReturnStatus.ReturnReadyForDeliveryToCustomer
];

export const ReturnModalDriverBlock = ({
  returnDetails,
  refetchDetails,
  refetchReturns
}: ReturnModalDriverBlockProps) => {
  const { t } = useTranslation();

  const canReassign = ASSIGNABLE_STATUSES.includes(returnDetails.status);

  // Default to the mode that matches the current assignment so admin lands
  // on the right tab — Company if a company is already on the order,
  // Driver otherwise. Mirrors OrderInfo's composite selector for parity
  // between regular orders and return pickups.
  const [assigneeMode, setAssigneeMode] = useState<"driver" | "company">(() =>
    returnDetails.deliveryCompanyId ? "company" : "driver"
  );

  const { isLoading: isLoadingAssignDriver, mutate: mutateAssignDriver } =
    useAssignDriver({
      onSuccess: () => {
        refetchDetails();
        refetchReturns();
      }
    });

  const { isLoading: isLoadingAssignCompany, mutate: mutateAssignCompany } =
    useAssignDeliveryCompany({
      onSuccess: () => {
        refetchDetails();
        refetchReturns();
      }
    });

  const { companies, isLoading: isLoadingCompanies } = useGetDeliveryCompanies(
    {
      skip: 0,
      take: 50,
      isDeleted: false
    },
    { enabled: canReassign }
  );

  const companyOptions =
    companies?.map((company) => ({
      label: company.name,
      value: company.id
    })) ?? [];

  const onSelectDriver = (driverId: string | string[]) => {
    const id = Array.isArray(driverId) ? driverId[0] : driverId;
    if (!id || id === returnDetails.driver?.id) return;
    mutateAssignDriver({ orderId: returnDetails.id, driverId: id });
  };

  const onSelectCompany = (deliveryCompanyId: string | string[]) => {
    const id = Array.isArray(deliveryCompanyId)
      ? deliveryCompanyId[0]
      : deliveryCompanyId;
    if (!id || id === returnDetails.deliveryCompanyId) return;
    mutateAssignCompany({ orderId: returnDetails.id, deliveryCompanyId: id });
  };

  if (
    returnDetails.status === ReturnStatus.ReturnRejected &&
    !returnDetails.driver &&
    !returnDetails.deliveryCompany
  ) {
    return null;
  }

  return (
    <div className={styles.returnModal_driverBlock}>
      <div className={styles.returnModal_driverBlock_title}>
        {t("Assignee")}
      </div>

      {canReassign ? (
        <>
          <div className={styles.returnModal_driverBlock_modeToggle}>
            <RadioButton
              label={t("Driver")}
              name="returnAssigneeMode"
              checked={assigneeMode === "driver"}
              onChange={() => setAssigneeMode("driver")}
            />
            <RadioButton
              label={t("Delivery company")}
              name="returnAssigneeMode"
              checked={assigneeMode === "company"}
              onChange={() => setAssigneeMode("company")}
            />
          </div>

          {assigneeMode === "company" ? (
            <>
              <Select
                placeholder={t("Choose Delivery Company")}
                value={returnDetails.deliveryCompanyId ?? undefined}
                onChange={onSelectCompany}
                options={companyOptions}
                isOptionsLoading={
                  isLoadingAssignCompany || isLoadingCompanies
                }
              />
              {returnDetails.deliveryCompanyId ? (
                <DriverSelector
                  value={returnDetails.driver?.id}
                  onChange={onSelectDriver}
                  isLoading={isLoadingAssignDriver}
                  deliveryCompanyId={returnDetails.deliveryCompanyId}
                />
              ) : null}
            </>
          ) : (
            <DriverSelector
              value={
                returnDetails.deliveryCompanyId
                  ? undefined
                  : returnDetails.driver?.id
              }
              onChange={onSelectDriver}
              isLoading={isLoadingAssignDriver}
            />
          )}
        </>
      ) : (
        <>
          {returnDetails.deliveryCompany ? (
            <PersonInfo
              image={returnDetails.deliveryCompany.avatar}
              personName={returnDetails.deliveryCompany.name}
              dimensions={{ width: 24, height: 24, borderRadius: "50%" }}
              nameClassName={styles.returnModal_driverBlock_name}
            />
          ) : null}
          {returnDetails.driver ? (
            <PersonInfo
              image={returnDetails.driver.image}
              personName={`${returnDetails.driver.firstname} ${returnDetails.driver.lastname}`}
              dimensions={{ width: 24, height: 24, borderRadius: "50%" }}
              nameClassName={styles.returnModal_driverBlock_name}
            />
          ) : null}
        </>
      )}
    </div>
  );
};
